import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../../../components/modal/Dialog";
import { Icon, IconType, Button } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { ReactNode, useState } from "react";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import { useLegalEntityId } from "~/hooks/api/v2/legalEntity/useLegalEntityId";
import { useLegalEntity } from "~/hooks/api/v2/legalEntity/useLegalEntity";
import { Alert } from "~/components/alert/Alert";

export type TwoSellarCreditPayStatementDialogProps = {
  active: boolean;
  onDismiss: () => void;
  dueDate: Date;
  amountOwed: number;
  paymentReference: string;
};

export const TwoSellarCreditPayStatementDialog = ({
  active,
  onDismiss,
  dueDate,
  amountOwed,
  paymentReference,
}: TwoSellarCreditPayStatementDialogProps) => {
  const [dialogPage, setDialogPage] = useState<"options" | "manual-transfer">(
    "options",
  );

  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    staleTime: 1000 * 60 * 5,
    enabled: !!legalEntityId,
  });

  const legalEntityAdminUserEmail =
    legalEntity?.legalEntityAdminUserList[0]?.email;

  const formattedDueDate = dayjs(dueDate).format("Do MMMM YYYY");

  const formattedAmountOwed = formatCurrency(amountOwed);

  const [previousActive, setPreviousActive] = useState(active);
  if (active !== previousActive) {
    setPreviousActive(active);
    if (!active) {
      setDialogPage("options");
    }
  }

  const bankTransferDetails = {
    accountHolderName: "Two B2B Ltd",
    accountNumber: "14715144",
    sortCode: "18-50-08",
    paymentReference,
    dueDate: formattedDueDate,
    amountOwed: formattedAmountOwed,
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={
          dialogPage === "manual-transfer"
            ? () => setDialogPage("options")
            : onDismiss
        }
        title={
          dialogPage === "manual-transfer"
            ? "Pay down your Sellar Credit tab - Bank transfer"
            : "Pay Sellar Credit statement"
        }
        description={
          dialogPage === "manual-transfer"
            ? "Use the details provided to make a manual bank transfer"
            : "You can pay your statement using one of the options below. Payment can be made on or before the due date."
        }
      />
      <DialogContent>
        {dialogPage === "options" ? (
          <div className="flex flex-col gap-2">
            <PayDownTabMethodOption
              icon="BANK"
              title="Make a manual bank transfer"
              description="Use the details provided to make a manual bank transfer"
              onClick={() => setDialogPage("manual-transfer")}
              action={
                <span className="flex items-center justify-center">
                  View details
                  <Icon icon="CHEVRON_RIGHT" size={1} />
                </span>
              }
            />

            <PayDownTabMethodOption
              icon="TWO_ICON"
              title="Visit the Two Inc. dashboard"
              description={
                <>
                  View and download your statement from our partner credit
                  provider who helps power Sellar Credit. Your login email will
                  be <b>{legalEntityAdminUserEmail}</b>
                </>
              }
              onClick={() => {
                const url =
                  import.meta.env.VITE_ENV === "production"
                    ? `https://buyer.two.inc/statements`
                    : `https://buyer.sandbox.two.inc/statements`;
                window.open(url, "_blank");
              }}
              action={
                <span className="flex items-center justify-center">
                  Go to dashboard
                  <Icon icon="CHEVRON_RIGHT" size={1} />
                </span>
              }
            />

            <div className="mt-2 flex w-full justify-end">
              <Button
                color={"secondary"}
                className={"w-48 p-4"}
                onClick={() =>
                  window.open(
                    "https://intercom.help/sellario/en/articles/11831214-buyer-guide-how-do-i-pay-my-sellar-credit-tab",
                    "_blank",
                  )
                }
              >
                <div className="flex items-center gap-1">
                  <Icon
                    icon="HELP_CIRCLE_OUTLINE"
                    size={1}
                    className="shrink-0"
                  />
                  VIEW HELP GUIDE
                </div>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Alert
              type="information"
              message={
                <>
                  <span className="font-semibold">Important:</span> You must
                  enter the payment reference indicated below when making your
                  bank transfer.
                </>
              }
            />

            <div className="rounded-lg bg-zinc-100 p-5">
              <div className="grid grid-cols-[minmax(180px,1fr)_1fr] gap-x-6 gap-y-4 text-lg text-zinc-700">
                <span className="font-semibold">Name of account holder</span>
                <span>{bankTransferDetails.accountHolderName}</span>

                <span className="font-semibold">Account number</span>
                <span>{bankTransferDetails.accountNumber}</span>

                <span className="font-semibold">Sort code</span>
                <span>{bankTransferDetails.sortCode}</span>

                <span className="font-semibold">
                  Payment reference (required)
                </span>
                <span className="font-semibold">
                  {bankTransferDetails.paymentReference}
                </span>

                <span className="font-semibold">Due date</span>
                <span className="font-semibold">
                  {bankTransferDetails.dueDate}
                </span>

                <span className="font-semibold">Amount owed</span>
                <span className="font-semibold">
                  {bankTransferDetails.amountOwed}
                </span>
              </div>
            </div>

            <p className="text-base text-zinc-700">
              <span className="font-semibold">Please note:</span> It may take up
              to 1-2 days for your payment to be reflected in your Sellar Credit
              tab.
            </p>

            <Button
              color="transparent"
              onClick={() => setDialogPage("options")}
              className="mt-2 w-fit"
            >
              <span className="flex items-center gap-2">
                <Icon icon="CHEVRON_LEFT" className="shrink-0" />
                BACK
              </span>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

type PayDownTabMethodOptionProps = {
  icon: IconType;
  action: ReactNode;
  title: string;
  description: ReactNode;
  tag?: ReactNode;
  footer?: ReactNode;
  onClick?: () => void;
};

const PayDownTabMethodOption = ({
  icon,
  action,
  title,
  description,
  tag,
  footer,
  onClick,
}: PayDownTabMethodOptionProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div
        onClick={onClick}
        className={twMerge(
          "flex flex-col justify-between gap-4 rounded-lg border border-zinc-200 bg-zinc-50 p-4 sm:flex-row sm:items-center",
          onClick && "cursor-pointer",
        )}
      >
        <div className="flex flex-1 items-center gap-2">
          <figure className="rounded-lg bg-zinc-100 p-2 text-zinc-800">
            <Icon icon={icon} size={1.3} />
          </figure>
          <div className="flex flex-col">
            <p className="font-semibold">
              {title} {Boolean(tag) && tag}
            </p>
            <p className="text-sm">{description}</p>
          </div>
        </div>
        {action}
      </div>
      {Boolean(footer) && footer}
    </div>
  );
};
